import * as ng from 'angular';
import 'angular-gettext';
import * as Linq from 'linq-es5';
import { ICustomerSettingsService } from './';
import { Enumerable, IEnumerable } from "../../node_modules/linq-es5/lib/enumerable";
import { IAccountResource, IProfileResource, IFrequentParkerAccountResource, Dto } from '../data';
import { ISiteConfigService } from '../core';
import * as Core from '../core';
import { IAuthService, ICredentials, IToken } from '../components/security';

let SessionServiceFactory: Function = function ($window: ng.IWindowService,
    $q: ng.IQService,
    $router: ng.Router,
    $timeout: ng.ITimeoutService,
    gettextCatalog: ng.gettext.gettextCatalog,
    SiteConfig: ISiteConfigService,
    Account: IAccountResource,
    Profile: IProfileResource,
    FrequentParkerAccount: IFrequentParkerAccountResource,
    Auth: IAuthService,
    CustomerSettings: ICustomerSettingsService): ISessionService {
    return new SessionService($window, $q, $router, $timeout, gettextCatalog, SiteConfig,
        Account, Profile, FrequentParkerAccount, Auth, CustomerSettings);
}

SessionServiceFactory.$inject = ['$window', '$q', '$rootRouter', '$timeout', 'gettextCatalog', 'SiteConfig',
    'Account', 'Profile', 'FrequentParkerAccount', 'Auth', 'CustomerSettings'];


let Session = {
    name: "Session",
    factory: SessionServiceFactory
};

interface ISessionService {

    createSession(credentials: ICredentials): ng.IPromise<ISessionState>;
    getSessionAsync(): ng.IPromise<ISessionState>;
    getLanguage(): ng.IPromise<Core.ILanguage>;
    isUserInRole(role): Boolean;
    isAccountHolder(): Boolean;
    isAdmin(): Boolean;
    setLanguage(language): void;
    clearLanguage(): void;
    reload(): ng.IPromise<ISessionState>;
    reset(): ISessionState;
    registerRefreshHandler(handler): void;
    notifyListeners(current): void;
    assureActiveUserSession(): void;
}

export interface ISessionState {
    account: Dto.IAccount;
    profile: Dto.IProfile;
    frequentParkerAccount: Dto.IFrequentParkerAccount;
    language: Core.ILanguage;
    userClaims: Map<string, Object>;
    tokens: Array<IToken>;
}
export class SessionService implements ISessionService {
    private storageKey: string = "sessionKeys";
    private languageStorageKey: string = "lang";
    private current: ISessionState = {
        account: null,
        profile: null,
        frequentParkerAccount: null,
        language: null,
        userClaims: null,
        tokens: null
    };

    private languages: Enumerable<Core.ILanguage>;
    private notifySessionRefreshHandlers: Array<Function> = [];

    constructor(private $window: ng.IWindowService,
        private $q: ng.IQService,
        private $router: ng.Router,
        private $timeout: ng.ITimeoutService,
        private gettextCatalog: ng.gettext.gettextCatalog,
        private SiteConfig: ISiteConfigService,
        private Account: IAccountResource,
        private Profile: IProfileResource,
        private FrequentParkerAccount: IFrequentParkerAccountResource,
        private Auth: IAuthService,
        private CustomerSettings: ICustomerSettingsService) {

        console.log('SessionService loading');

        //activate service
        this.activate();
    }

    //functions
    activate(): void {
        console.log('SessionService loaded');
    }

    registerRefreshHandler(handler): void {
        if (typeof handler === 'function')
            this.notifySessionRefreshHandlers.push(handler);
    }

    notifyListeners(current): void {
        Linq.From(this.notifySessionRefreshHandlers).ToArray()
            .forEach((x) => {
                try {
                    x(current);
                } catch (e) {
                    console.log(e);
                }
            });
    }

    public reload(): ng.IPromise<ISessionState> {
        return this.getAccountHolderSessionAsync();
        //notifyListeners(session.current);
    }

    public reset(): ISessionState {
        var language = this.current.language;
        this.current = {} as ISessionState;
        this.current.language = language;
        this.clearSessionTokenKeys();
        this.Auth.clearTokens();
        return this.current;
    }

    private clearSessionTokenKeys() {
        this.$window.sessionStorage.removeItem(this.storageKey);
    }

    public createSession(credentials: ICredentials): ng.IPromise<ISessionState> {
        let client = this.SiteConfig.getClientByType("user");

        return this.Auth.getUserTokenAsync(client.id, credentials.scope, credentials.username, credentials.password)
            .then((token: IToken) => {
                let claims = token.claims;
                if (this.roleClaimExists(claims, "AccountHolder")) {
                    return this.getAccountHolderSessionAsync().then((context) => {
                        this.current.userClaims = claims;
                        this.setTokenOnContext(this.current, token);
                        this.accountHolderCheckCompleted = true;
                        return this.$q.resolve(this.current);
                    });
                } else {

                    if (token && token.claims && token.claims["name"]) {
                        return this.Profile.getAdmin({                          
                            id: token.claims["sub"]
                        }).$promise.then((profileResponse) => {
                            if (profileResponse)
                                this.current.profile = profileResponse;

                            var umCredentials = ng.copy(credentials);
                            umCredentials.scope = "HubUSA.Core.Identity";
                            return this.Auth.getUserTokenAsync(client.id, umCredentials.scope, umCredentials.username, umCredentials.password)
                                .then((umToken: IToken) => {

                                    this.current = this.current || {} as ISessionState;
                                    this.current.userClaims = claims;
                                    this.setTokenOnContext(this.current, umToken);
                                    this.setTokenOnContext(this.current, token);
                                    this.accountHolderCheckCompleted = true;
                                    return this.$q.resolve(this.current);
                                });
                        });
                    }                   
                }
            });
    }

    public createSessionFromTokens(tokens: Array<IToken>): ng.IPromise<ISessionState> {
        this.current.tokens = this.getTokensFromStorage();
        if (this.current.tokens) {
            var userToken = Linq.From(this.current.tokens).FirstOrDefault(x => x.tokenType == "User");
            let afterLoad = () => {
                this.current = this.current || {} as ISessionState;
                this.current.userClaims = userToken.claims;

                if (!this.current.language) {
                    return this.getLanguage().then(() => {
                        return this.$q.resolve(this.current);
                    });
                }
                else return this.$q.resolve(this.current);
            };

            if (userToken) {
                if (this.roleClaimExists(userToken.claims, "AccountHolder")) {
                    return this.getAccountHolderSessionAsync().then((context) => {
                        return afterLoad();
                    });
                } else {
                    if (userToken.claims && userToken.claims["name"]) {
                        return this.Profile.search({
                            isAdmin: true,
                            userName: userToken.claims["name"]
                        } as Dto.IProfileCriteria).$promise.then((profileResponse) => {
                            if (profileResponse && profileResponse.length > 0)
                                this.current.profile = profileResponse[0];
                            return afterLoad();
                        });
                    }
                }
            }
            
        }

        return this.getLanguage().then(() => {
            return this.$q.resolve(this.current);
        });
    }

    private setTokenOnContext(context: ISessionState, token: IToken) {

        context.tokens = context.tokens || [];
        context.tokens.push(token);

        let storedJson = this.$window.sessionStorage.getItem(this.storageKey);
        let storedKeys = typeof storedJson === 'string'
            ? JSON.parse(storedJson)
            : { tokenKeys: [] };

        if (Linq.from(storedKeys.tokenKeys).Contains(token.key)) return;

        storedKeys.tokenKeys.push(token.key);
        this.$window.sessionStorage.setItem(this.storageKey, JSON.stringify(storedKeys));
    }

    private getTokensFromStorage(): Array<IToken> {
        let storedJson = this.$window.sessionStorage.getItem(this.storageKey);
        try {
            let storedKeys = JSON.parse(storedJson);
            var tokenKeys = storedKeys.tokenKeys || [];
            return Linq.From(tokenKeys)
                .Select(x => this.Auth.getToken(x))
                .Where(x => x !== null)
                .ToArray();
        }
        catch (e) {
            return null;
        }
    }

    private claimExists(claims: Map<string, Object>, claimType: string, claim: string): Boolean {
        var existingClaim = claims[claimType];
        if (typeof existingClaim === "string")
            return claim === existingClaim;
        else if (Array.isArray(existingClaim)) {
            return Linq.from(existingClaim).Contains(claim);
        }
    }

    private roleClaimExists(claims: Map<string, Object>, role: string): Boolean {
        return this.claimExists(claims, "role", role);
    }

    public isUserInRole(role: string): Boolean {

        return this.current
            && this.current.userClaims
            && this.roleClaimExists(this.current.userClaims, role);
    }

    public isAccountHolder(): Boolean {
        return this.isUserInRole("AccountHolder");
    }

    public isAdmin(): Boolean {
        return this.isUserInRole("Admin");
    }

    private sessionPromise: ng.IPromise<ISessionState> = null;
    private accountHolderCheckCompleted: boolean = false;
    public getSessionAsync(): ng.IPromise<ISessionState> {
        if (this.accountHolderCheckCompleted
            && this.current && this.current.tokens
            && this.current.tokens.length > 0) {
            this.sessionPromise = null;
            return this.$q.resolve(this.current);
        }
        else {

            if (this.sessionPromise)
                return this.sessionPromise;

            this.sessionPromise = this.createSessionFromTokens(this.getTokensFromStorage());

            return this.sessionPromise;
        }
    }

    //rename to getCustomerSession
    private getAccountHolderSessionAsync(): ng.IPromise<ISessionState> {

        // http://stackoverflow.com/questions/23145688/ie10-11-ajax-xhr-error-script7002-xmlhttprequest-network-error-0x2ef3/32106649#32106649
        // For me the issue seemed to be occurring only in IE11 over a Post call, I have been using 
        // AngularJS provider $http to make the call. It seems that this is a bug in IE, and it is a 
        // certificate issue. The security certificate information is not loaded properly when the POST 
        // call is made. As a workaround, before making the POST call, make a GET call to load the 
        // certificate properly.
        return this.Account.get({}).$promise.then(() => {
            return this.Account.query({})
                .$promise
                .then((response) => {
                    var lookups = response || [];
                    var firstLookup = Linq.From(lookups).First() as any;
                    return this.loadAccount(firstLookup.id).then((account) => {
                        this.current.account = account || null;
                        console.log('Successful Account load from API, Name is: ' + account.name);

                        return this.loadProfile(account.id).then((profile: Dto.IProfile) => {
                            this.current.profile = profile;
                            let langCode = profile.language || this.current.language.code;
                            this.gettextCatalog.setCurrentLanguage(langCode);

                            return this.loadFrequentParkerAccount(account.id, profile.id).then((frequentParkerAccount) => {
                                this.current.frequentParkerAccount = frequentParkerAccount;
                                return this.$q.resolve(this.current);
                            });
                        });
                    });
                })
                .catch((e) => {
                    this.logException(e, 'SessionService: Failed load of Account list for current user');
                    return this.getLanguage().then((language) => {
                        this.current.language = language
                        return this.$q.resolve(this.current);
                    });

                })
                .finally(() => {
                    this.notifyListeners(this.current);
                });

        }).catch((e) => {
            this.logException(e, 'SessionService: Failed load of Account');
            return this.getLanguage().then((language) => {
                this.current.language = language
                    || this.current.language
                    || (this.languages)
                    ? this.languages.FirstOrDefault(y => y.isDefault)
                    : null;
                return this.$q.resolve(this.current);
            });
        });
    }

    private loadAccount(accountId: string): ng.IPromise<Dto.IAccount> {
        return this.Account.get({ id: accountId, eagerLoad: true }).$promise
            .then((response) => {
                return this.$q.resolve(response || null);
            })
            .catch((e) => {
                this.logException(e, 'Failed load of Account for: ' + accountId);
                return this.$q.reject(null);
            });
    }

    private loadProfile(accountId): ng.IPromise<Dto.IProfile> {
        return this.Profile.get({ accountId: accountId }).$promise
            .then((profile) => {
                console.log('Profile.firstName: ' + profile.firstName);
                return this.$q.resolve(profile || null);
            })
            .catch((e) => {
                this.logException(e, 'Failed load of Profile for currently logged on user');
                return this.$q.reject(null);
            })
            .finally(() => {
                return this.notifyListeners(this.current);
            });
    }

    private loadFrequentParkerAccount(accountId, userProfileId): ng.IPromise<Dto.IFrequentParkerAccount> {

        var criteria = {
            accountId: accountId,
            userProfileId: userProfileId
        };

        return this.FrequentParkerAccount.query({ accountId: accountId }, criteria).$promise
            .then((frequentParkerAccounts) => {
                var frequentParkerAccount = Linq.From(frequentParkerAccounts).FirstOrDefault();
                return this.$q.resolve(frequentParkerAccount || null);
            })
            .catch((e) => {
                this.logException(e, 'Failed load of FrequentParkerAccount for currently logged on user');
                return this.$q.reject(null);
            });
    }

    public getLanguage(): ng.IPromise<Core.ILanguage> {
        return this.CustomerSettings.getLanguageSettings()
            .then((languageSettings) => {
                this.languages = this.languages || Linq.From(languageSettings.languages);

                if (!this.current.language) {
                    //get language from profile if its there.
                    if (this.current.profile && this.current.profile.language) {
                        this.current.language = this.languages
                            .FirstOrDefault((x: any) => x.code === this.current.profile.language);
                        this.setLanguage(this.current.language);
                    }
                    else {
                        //check local storage
                        var lang = this.$window.localStorage.getItem(this.languageStorageKey);
                        if (lang) {
                            lang = JSON.parse(lang);
                            let langObj = this.getValidLanguageSelection(lang)
                            if (langObj) {
                                this.setLanguage(langObj);
                                return this.current.language;
                            }
                        }

                        //detect language
                        var detectedLangCode = (window.navigator as any).userLanguage || window.navigator.language;
                        detectedLangCode = detectedLangCode.split("-")[0];
                        var detectedLanguage = this.languages
                            .FirstOrDefault((x: any) => x.code === detectedLangCode);
                        this.setLanguage(detectedLanguage);
                    }
                }

                return this.$q.resolve(this.current.language);
            });
    }

    public setLanguage(language): void {

        this.CustomerSettings.getLanguageSettings()
            .then((languageSettings) => {
                this.languages = this.languages || Linq.From(languageSettings.languages);
                var langObj = this.getValidLanguageSelection(language)
                if (langObj) {
                    this.$window.localStorage.setItem(this.languageStorageKey, JSON.stringify(langObj));
                    this.current.language = langObj;
                    this.gettextCatalog.setCurrentLanguage(this.current.language.code);//this.$timeout(() => {}, 2);

                    if (this.current.profile && this.current.profile.language !== langObj.code) {
                        this.current.profile.language = langObj.code;
                        this.Profile.update({
                            accountId: this.current.account.id,
                            id: this.current.profile.id
                        }, this.current.profile);
                    }
                }
            });
    }

    private getValidLanguageSelection(language: Core.ILanguage | string): Core.ILanguage {
        var langCode = (typeof language === 'string')
            ? language
            : (language) ? language.code : 'en';

        return this.languages.FirstOrDefault((x: Core.ILanguage) => langCode === x.code);
    }

    private logException(e, customMessage): void {
        customMessage = customMessage || '';
        console.log(customMessage);
        console.log(e);
    }

    public clearLanguage(): void {
        this.$window.localStorage.removeItem(this.languageStorageKey);
    }

    public assureActiveUserSession(): boolean {
        if (!this.current.tokens) {
            this.$router.navigate(['SignIn']);
            return false;
        } 
        return true;
    }
}

export { Session, ISessionService };
export default Session;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/services/session.ts